import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { distinctUntilChanged, forkJoin, of, Subject, switchMap, takeUntil } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from '@services/notification.service';
import { RecipeService } from '@services/recipe/recipe.service';
import { RefDataService } from '@services/ref-data.service';
import { Market, Language, Domain } from '@shared/models/dataref.model';
import { TripletIdLite } from '@shared/models/generic/id/triplet-id-lite.model';
import { RecipeLite } from '@shared/models/recipe/recipe-lite.model';
import { map, tap } from 'rxjs/operators';

@Component({
  selector: 'app-copy-recipe-popin',
  templateUrl: './copy-recipe-popin.component.html',
  styleUrls: ['./copy-recipe-popin.component.scss'],
  standalone: false,
})
export class CopyRecipePopinComponent implements OnInit, OnDestroy {
  formCopy = this.formBuilder.group({
    sourceDomain: ['', Validators.required],
    sourceMarket: ['', Validators.required],
    sourceLang: ['', Validators.required],
    sourceTitle: ['', Validators.required],
    targetDomain: ['', Validators.required],
    targetMarket: ['', Validators.required],
    targetLang: ['', Validators.required],
    targetTitle: ['', Validators.required],
    synchro: [''],
    translationRequested: [false],
  });
  domains: Domain[] = [];
  markets: Market[] = [];
  sourceMarket: Market;
  languages: Language[] = [];
  languagesFilteredSources: Language[] = [];
  languagesFilteredTarget: Language[] = [];
  variantList: RecipeLite[] | null = [];
  isAuthorizeSynchro = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly refDataService: RefDataService,
    private readonly recipeService: RecipeService,
    private readonly notificationService: NotificationService,
    private readonly formBuilder: UntypedFormBuilder,
    public dialogRef: MatDialogRef<CopyRecipePopinComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { variantList: RecipeLite[]; recipeOriginal: RecipeLite },
  ) {
    this.variantList = data.variantList;
  }

  ngOnInit(): void {
    this.formCopy = this.formBuilder.group({
      sourceDomain: [{ value: this.data.recipeOriginal?.domain, disabled: true }, Validators.required],
      sourceMarket: [{ value: this.data.recipeOriginal?.market, disabled: true }, Validators.required],
      sourceLang: [this.data.recipeOriginal?.lang, Validators.required],
      sourceTitle: [{ value: this.data.recipeOriginal?.title, disabled: true }, Validators.required],
      targetDomain: ['', Validators.required],
      targetMarket: [{ value: '', disabled: true }, Validators.required],
      targetLang: [{ value: '', disabled: true }, Validators.required],
      targetTitle: [this.data.recipeOriginal?.title, Validators.required],
      synchro: false,
      translationRequested: false,
    });
    const queries = [
      this.refDataService.getByKey('domains'),
      this.refDataService.getByKey('languages'),
      this.refDataService.getByKey('markets'),
    ];
    forkJoin(queries).subscribe((responses) => {
      this.domains = this.refDataService.translateReference(this.refDataService.toArray(responses[0]));
      this.languages = this.refDataService.translateReference(this.refDataService.toArray(responses[1]));
      this.markets = this.refDataService.translateReference(this.refDataService.toArray(responses[2]));
      this.updateSourceMarket();
      this.updateLang(this.data.recipeOriginal?.market, true);
    });

    if (localStorage.getItem('profile')) {
      const roleUser = JSON.parse(localStorage.getItem('profile') ?? '')?.authorizations?.role;
      this.isAuthorizeSynchro = roleUser === 'ROLE_199' || roleUser === 'ROLE_105';
    }

    //Uncheck variant synchronisation when recipe traduction checked
    this.formCopy
      .get('translationRequested')
      ?.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((result) => {
        this.formCopy.controls.synchro[result ? 'disable' : 'enable']();
      });

    //Uncheck recipe traduction when variant synchronisation checked
    this.formCopy
      .get('synchro')
      ?.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((result) => {
        this.formCopy.controls.translationRequested[result ? 'disable' : 'enable']();
      });

    this.formCopy.controls.translationRequested.disable();

    //Handle recipe translation
    this.formCopy
      .get('targetLang')
      ?.valueChanges.pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        tap((targetLang: string | null) => {
          //Reset checkbox value when targetLang is changed
          this.formCopy.controls.translationRequested.setValue(false);
          this.formCopy.controls.synchro.setValue(false);
          if (targetLang) {
            this.formCopy.controls.translationRequested.enable();
          } else {
            this.formCopy.controls.translationRequested.disable();
          }
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  submit(): void {
    if (this.formCopy.invalid || this.variantList === null) {
      return;
    }

    const recipes: TripletIdLite[] = [];

    // find the variant with the right market and lang
    this.variantList.forEach((recipe) => {
      if (this.formCopy.controls.sourceLang.value === recipe.lang && this.formCopy.controls.sourceMarket.value === recipe.market) {
        recipes.push({
          functionalId: recipe.identifier.functionalId,
          sourceSystem: recipe.identifier.sourceSystem,
          version: recipe.identifier.version,
        });
      }
    });
    const payload = {
      domain: this.formCopy.controls.targetDomain.value,
      market: this.formCopy.controls.targetMarket.value,
      language: this.formCopy.controls.targetLang.value,
      title: this.formCopy.controls.targetTitle.value,
      synchro: this.formCopy.controls.synchro.value,
      translationRequested: this.formCopy.controls.translationRequested.value,
    };

    if (recipes.length) {
      this.recipeService
        .createVariant(recipes[0], payload)
        .pipe(
          switchMap((recipeTripletIdLite) => {
            if (payload.translationRequested) {
              const { sourceSystem, functionalId } = recipeTripletIdLite;
              return this.recipeService.getRecipe(`${sourceSystem}/${functionalId}`).pipe(
                map((recipe: RecipeLite | undefined) => ({
                  translationRequested: payload.translationRequested,
                  topRecipeId: recipe?.topRecipeId,
                })),
              );
            } else {
              return of({
                recipeId: recipeTripletIdLite,
              });
            }
          }),
        )
        .subscribe({
          next: (dialogResult) => {
            this.notificationService.sendSuccess('ERRORS_RECIPE_SAVED');
            this.dialogRef.close(dialogResult);
          },
          error: (err) => this.errorManager(err),
        });
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  // filter available language with the market selected, and select one if only one in the list
  updateLang(currentMarket: string, isSource: boolean): void {
    const market = this.markets.find((m) => m.key === currentMarket);
    if (market) {
      const languagesFiltered = this.refDataService.translateReference(Object.values(market.langs));
      if (isSource) {
        this.languagesFilteredSources = languagesFiltered;
        if (this.languagesFilteredSources.length === 1) {
          this.formCopy.controls.sourceLang.setValue(this.languagesFilteredSources[0].key);
        }
      } else {
        this.languagesFilteredTarget = languagesFiltered;
        if (this.languagesFilteredTarget.length === 1) {
          this.formCopy.controls.targetLang.setValue(this.languagesFilteredTarget[0].key);
        } else {
          this.formCopy.controls.targetLang.reset('');
        }
        this.formCopy.get('targetLang')?.enable();
      }
    } else {
      this.formCopy.get('targetLang')?.disable();
    }
  }

  updateDomain(domain: string): void {
    this.formCopy.controls.targetDomain.setValue(domain);
    this.formCopy.get('targetMarket')?.enable();
    this.formCopy.controls.targetMarket.setValue('');
    this.formCopy.get('targetLang')?.disable();
    this.formCopy.controls.targetLang.setValue('');
    this.refDataService.getByKey('markets', false, domain).subscribe((markets) => {
      this.markets = this.refDataService.translateReference(this.refDataService.toArray(markets));
    });
  }

  private errorManager(err: any): void {
    if (err.status && err.status === 403) {
      this.notificationService.sendError('COPY_RECIPES_MARKET.MODAL.MSG.ERROR.FORBIDDEN');
    } else {
      this.notificationService.sendAPIError(err, 'VARIANT_CREATION_FAILED');
    }
  }

  private updateSourceMarket(): void {
    this.sourceMarket = this.markets.find((market) => market.key === this.data.recipeOriginal?.market) ?? ({} as Market);
  }
}
